import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import { loadCommissionerCommunicationsTypes } from '../../../store/directusService';
import { loadContractConstants, updateContractConstants } from '../../../store/participantService';
import { getObjFromId } from '../../../utils/directusFunctions';
import { NO_CONTRACT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import {
    COMMISSIONER_COMMUNICATIONS_TYPES_ERROR,
    CONTRACT_CONSTANTS_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { trimEntries } from '../../../utils/objectUtils';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import AccordionHeader from '../../ui/accordion/AccordionHeader';
import AccordionIcon from '../../ui/accordion/AccordionIcon';
import LoadingSpinner from '../../ui/LoadingSpinner';

import DocmanTriggerAdmin from './docmanAdmin/DocmanTriggerAdmin';
import { validate } from './validateCommissionerCommunications';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';

const CommissionerCommunicationsAdmin = ({ contractId }) => {
    const dispatch = useDispatch();

    const initialState = {
        commissionerCommunicationIds: []
    };

    // LOCAL STATE
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialEntryState, setInitialEntryState] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [expanded, setExpanded] = useState('');

    // STORE STATE
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );
    const commissionerCommunicationsTypes = useSelector(
        (state) => state.entities.directusService.commissionerCommunicationsTypes
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        !commissionerCommunicationsTypes?.length && dispatch(loadCommissionerCommunicationsTypes());
    }, []);

    useEffect(() => {
        if (!contractId) return;
        if (contractId !== contractConstants?.contractId)
            dispatch(loadContractConstants(contractId));
        else {
            setNewEntry({ ...initialState, ...contractConstants });
            setInitialEntryState(contractConstants);
        }
    }, [
        contractId,
        contractConstants?.contractId,
        contractConstants?.commissionerCommunicationIds
    ]);

    useEffect(() => {
        if (successMessage === 'Contract constants has been updated')
            setIsUpdateButtonDisabled(false);
    }, [successMessage]);

    // HELPER FNS
    const clearError = (key) => {
        errors[key] && setErrors(trimEntries([key], errors));
    };

    const loadingError = () => {
        if (!contractId.length) return NO_CONTRACT_ERROR;
        if (!commissionerCommunicationsTypes?.length)
            return COMMISSIONER_COMMUNICATIONS_TYPES_ERROR;
        if (!Object.keys(contractConstants).length) return CONTRACT_CONSTANTS_ERROR;
    };

    const getCommissionerCommIdByName = (name) => {
        return contractConstants.commissionerCommunicationIds?.includes(
            commissionerCommunicationsTypes.find(
                (el) => el.name.toLowerCase() === name.toLowerCase()
            )?.id || ''
        );
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onPanelChange = (panel) => {
        setExpanded(expanded === panel ? '' : panel);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(newEntry, initialEntryState);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }
        dispatch(updateContractConstants(newEntry));
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div>
            <div className={`${form.formWrapper} ${app.fadein}`}>
                <form
                    className={form.form}
                    onSubmit={onSubmit}
                    data-testid="edit_docman_trigger_form">
                    <div className={form.formSection}>
                        <MultiSelect
                            id={'commissionerCommunicationIds'}
                            label="Commissioner Communications"
                            placeholder="Select communication type..."
                            menuItems={commissionerCommunicationsTypes || []}
                            preSelectedIds={newEntry?.commissionerCommunicationIds || []}
                            preSelects={newEntry?.commissionerCommunicationIds?.map((el) =>
                                getObjFromId(commissionerCommunicationsTypes, el)
                            )}
                            onChange={(chosenIds) =>
                                onUpdate('commissionerCommunicationIds', chosenIds)
                            }
                        />
                    </div>
                    <Button
                        id="commissionerCommunicationsUpdateButton"
                        disabled={isUpdateButtonDisabled}
                        content="UPDATE"
                        error={errors.button}
                        clearError={() => clearError('button')}
                    />
                </form>
            </div>
            {getCommissionerCommIdByName('docman') && (
                <div>
                    <Accordion
                        expanded={expanded === 'docman'}
                        onChange={() => onPanelChange('docman')}
                        slotProps={{ transition: { mountOnEnter: true } }}
                        className={app.accordionDivider}
                        data-testid="accordion-docman">
                        <AccordionSummary
                            expandIcon={<AccordionIcon />}
                            aria-controls="docman-accordion-content"
                            id="docman-accordion-header">
                            <AccordionHeader>Docman</AccordionHeader>
                        </AccordionSummary>
                        <AccordionDetails>
                            {expanded === 'docman' && (
                                <DocmanTriggerAdmin contractId={contractId} />
                            )}
                        </AccordionDetails>
                    </Accordion>
                </div>
            )}
        </div>
    );
};

CommissionerCommunicationsAdmin.propTypes = {
    contractId: PropTypes.string
};

export default CommissionerCommunicationsAdmin;
